/* eslint @typescript-eslint/camelcase: 0 */
import isEqual from 'lodash/isEqual';
import max from 'lodash/max';
import min from 'lodash/min';
import pick from 'lodash/pick';
import moment from 'moment';
import {Moment} from 'moment';

import {parseDate} from '../util';
import {
  getLiteralTrees,
  getLogicalOperators,
  getSingleClauseLiteralTrees,
  getSingleClauseLogicalOperators,
  isCohortLiteralTree,
  isEventLiteralTree,
  onlyLeftAssociatedAndOperators,
} from '../util/expression-tree';
import {TrackingEventPropertyName} from '../util/tracking';
import PeopleLiteral from './builder/people-literal';
import {LITERAL_NAMES} from './constants';
import OperatorNode from './expression/operator-node';
import {expressionToLiteralRoot} from './expression/utils';
import {CohortId} from './types/expression-types';
import {Serializable, SerializedCohort, User} from './types/serialized-cohort-types';
import {Trackable} from './types/tracking';
import DateRange from '../../../widgets/date-range-picker/models/date-range';

import {Behavior, BehaviorsDictionary} from './behaviors/behavior';
import CohortClause from './builder/cohort-clause';
import CohortLiteral from './builder/cohort-literal';
import EventLiteral from './builder/event-literal';
import StagedCohort from './builder/staged-cohort';

interface CohortOptions {
  behaviors: BehaviorsDictionary;
  count?: number;
  createdBy?: User;
  dependentCampaignIds?: number[];
  dependentCohortIds?: number[];
  description?: string;
  id?: number;
  isNew?: boolean;
  lastEdited?: Moment;
  lastQueried?: Moment;
  reportName?: string;
  name: string;
  selector: OperatorNode;
  dataGroupId?: string;
  hidden?: boolean;
  locked?: boolean;
}

export default class Cohort implements Serializable, Trackable {
  public static parseCohort(serializedCohort: SerializedCohort): Cohort {
    const {
      id,
      name,
      description,
      count,
      created_by,
      selector,
      data_group_id,
      last_edited,
      last_queried,
      referenced_by_campaigns,
      referenced_by,
      is_visible,
      is_locked,
    } = serializedCohort;
    const behaviors = serializedCohort.behaviors || {};
    return new Cohort({
      behaviors: Behavior.parseBehaviors(behaviors),
      count,
      createdBy: {
        email: created_by ? created_by.email : null,
        name: created_by ? created_by.name : null,
        id: created_by ? created_by.id : null,
      },
      dependentCampaignIds: referenced_by_campaigns || [],
      dependentCohortIds: referenced_by || [],
      description,
      id,
      lastEdited: moment(last_edited),
      lastQueried: last_queried ? moment(last_queried) : null,
      name,
      reportName: Behavior.getReportName(behaviors),
      selector: OperatorNode.parseExpression(selector) as OperatorNode,
      ...(data_group_id ? {dataGroupId: data_group_id.toString()} : {}),
      hidden: !is_visible,
      locked: is_locked,
    });
  }

  public static createCohortFromStaged(stagedCohort: StagedCohort): Cohort {
    const {name, description, id, dataGroupId, hidden, locked, createdBy} = stagedCohort;
    const behaviors = stagedCohort.eventLiterals.reduce(
      (dict, literal) => ({...dict, ...literal.behaviorsDictionary}),
      {},
    );
    return new Cohort({
      behaviors,
      description,
      id,
      name,
      selector: stagedCohort.getExpressionTree(),
      dataGroupId,
      hidden,
      locked,
      createdBy,
    });
  }

  public static createUsersInCohort({cohortId, dataGroupId}: {cohortId: CohortId; dataGroupId?: string}): Cohort {
    return new Cohort({
      behaviors: {},
      name: null,
      selector: expressionToLiteralRoot(new CohortClause({cohortId}).getExpressionTree()),
      ...(dataGroupId ? {dataGroupId} : {}),
    });
  }

  public readonly id: number;
  public isNew = false;
  public name: string;
  public description: string;
  public createdBy: User;
  public count: number;
  public lastEdited: Moment;
  public lastQueried: Moment;
  public reportName: null;
  public selector: OperatorNode;
  public behaviors: BehaviorsDictionary;
  public dependentCampaignIds: number[] = [];
  public dependentCohortIds: number[] = [];
  public dataGroupId?: string;
  public hidden: boolean;
  public locked: boolean;

  constructor(options: CohortOptions) {
    Object.assign(this, options);
  }

  public toStagedCohort(): StagedCohort {
    /**
     * The top-down orientation of the builder limits the kind of selector trees that it can represent.
     * For instance, we cannot render an expression of (A or B) and C with the current UI. We can translate
     * this constraint to a tree representation that noting that any "and" operator must not have an "or"
     * operator as one of its children.
     */
    if (!onlyLeftAssociatedAndOperators(this.selector)) {
      throw new Error(`cannot render provided cohort selector tree`);
    }

    let literalTrees = getLiteralTrees(this.selector);
    let operators = getLogicalOperators(this.selector);
    if (!literalTrees.length) {
      console.warn(`cohort expression tree not built by cohort builder`);
      const selectorWithParents = this.selector.assignParents();
      literalTrees = getSingleClauseLiteralTrees(selectorWithParents);
      operators = getSingleClauseLogicalOperators(selectorWithParents);
    }

    const literals = literalTrees.map((tree, idx) => {
      const labelPrefix = LITERAL_NAMES[idx];
      // TODO: investigate moving this to the Literal parent
      if (isEventLiteralTree(tree)) {
        return EventLiteral.createFromParts({tree, labelPrefix, behaviors: this.behaviors});
      } else if (isCohortLiteralTree(tree)) {
        return CohortLiteral.createFromParts({tree, labelPrefix});
      } else {
        return PeopleLiteral.createFromParts({tree, labelPrefix});
      }
    });

    return new StagedCohort({
      dataGroupId: this.dataGroupId,
      description: this.description,
      id: this.id,
      literals,
      name: this.name,
      operators,
      locked: this.locked,
      hidden: this.hidden,
      createdBy: this.createdBy,
    });
  }

  get creatorName() {
    if (this.createdBy.name) {
      return this.createdBy.name.trim();
    }
    if (this.createdBy.email) {
      return this.createdBy.email;
    }
    return ``;
  }

  public removeDependentCohortId(cohortId: number): Cohort {
    const dependentCohortIds = this.dependentCohortIds.filter((id) => id !== cohortId);
    return this.newCohort({dependentCohortIds});
  }

  public setCount(count: number): Cohort {
    return this.newCohort({count, lastQueried: moment(new Date())});
  }

  public setDescription(description: string): Cohort {
    return this.newCohort({description, isNew: false, lastEdited: moment(new Date())});
  }

  public setHidden(hidden: boolean): Cohort {
    return this.newCohort({hidden, isNew: false, lastEdited: moment(new Date())});
  }

  public setLocked(locked: boolean): Cohort {
    return this.newCohort({locked, isNew: false, lastEdited: moment(new Date())});
  }

  public showNewIndicator(isNew = true): Cohort {
    return this.newCohort({isNew});
  }

  public isEqual(otherCohort: Cohort): boolean {
    return isEqual(this.serialize(), otherCohort.serialize());
  }

  public serialize(): SerializedCohort {
    const behaviors = Object.keys(this.behaviors).reduce((acc, behaviorName) => {
      return {...acc, [behaviorName]: this.behaviors[behaviorName].serialize()};
    }, {});

    return {
      behaviors,
      description: this.description,
      name: this.name,
      selector: this.selector.serialize(),
      ...(this.dataGroupId ? {data_group_id: parseInt(this.dataGroupId, 10)} : {}),
      is_visible: !this.hidden,
      is_locked: this.locked,
    };
  }

  public toTrackingData(): object {
    const stagedCohort = this.toStagedCohort();
    const cohortTrackingData = {
      'cohort edited': this.lastEdited.format(`YYYY-MM-DD`),
      [TrackingEventPropertyName.UserCount]: this.count,
    };

    return {...cohortTrackingData, ...stagedCohort.toTrackingData()};
  }

  public toPerformanceTrackingData(): object {
    const stagedCohort = this.toStagedCohort();
    const getDaysQueried = (behaviorsList: Behavior[]): number => {
      if (!behaviorsList || !behaviorsList.length) {
        return 0;
      }

      const dateRanges = behaviorsList.map((behavior) => {
        const {fromDate, toDate} = behavior.getDateRange();
        const fromDateStr = DateRange.convertBaseDateToCalendarDate(fromDate);
        const toDateStr = DateRange.convertBaseDateToCalendarDate(toDate);
        return {fromDate: parseDate(fromDateStr, {iso: true}), toDate: parseDate(toDateStr, {iso: true})};
      });
      const minFromDate = moment.utc(min(dateRanges.map((range) => range.fromDate)));
      const maxToDate = moment.utc(max(dateRanges.map((range) => range.toDate)));
      return maxToDate.diff(minFromDate, `days`);
    };

    const behaviors = Object.values(this.behaviors);
    const totalDaysQueried = getDaysQueried(behaviors);
    const emptySelectorDays = getDaysQueried(behaviors.filter((b) => b.producesEmptySelector()));
    const performanceTrackingData = {
      'total days queried': totalDaysQueried,
      'total days queried with empty selector': emptySelectorDays,
    };

    return {...performanceTrackingData, ...stagedCohort.toTrackingData()};
  }

  private newCohort(options: Partial<CohortOptions>): Cohort {
    const oldOptions: CohortOptions = pick(this, [
      `behaviors`,
      `count`,
      `createdBy`,
      `dataGroupId`,
      `description`,
      `dependentCampaignIds`,
      `dependentCohortIds`,
      `id`,
      `isNew`,
      `lastEdited`,
      `reportName`,
      `name`,
      `selector`,
      `hidden`,
      `locked`,
    ]);
    return new Cohort({...oldOptions, ...options});
  }
}
